import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import styles from "./Base.module.scss";

import {
  BudgetFormData,
  useClientCurrentMonthBudgetCapSchema,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import { campaignJobBoardQueryKeys } from "@/features/campaigns/jobboards/api/queryKeys";
import BudgetForm from "@/features/transactions/components/BudgetForm";
import { ClientBudgetMonthProvider } from "@/lib/contexts/ClientBudget/ClientBudgetMonthProvider";
import { useClientBudgetMonth } from "@/lib/contexts/ClientBudget/useClientBudgetMonth";
import { usePopup } from "@/lib/hooks/usePopup";
import useToast from "@/lib/hooks/useToast";
import { queryClient } from "@/main";
import { GetClientResponse } from "@/types/response/GetClientResponse";

interface EditBudgetCapPopupProps {
  budget: number;
  clientId: string;
  month: string;
  onSuccess: () => void;
  year: string;
}

const EditBudgetCapPopupInner = ({
  budget,
  clientId,
  month,
  onSuccess,
  year,
}: EditBudgetCapPopupProps) => {
  const { disableClose, enableClose, closePopup } = usePopup();
  const { t } = useTranslation();
  const { updateBudgetCap } = useClientBudgetMonth();
  const { showToast } = useToast();
  const { clientCurrentMonthBudgetCapSchema } =
    useClientCurrentMonthBudgetCapSchema();

  const methods = useForm<BudgetFormData>({
    resolver: zodResolver(clientCurrentMonthBudgetCapSchema),
    mode: "onChange",
    defaultValues: {
      budget: budget || 0,
    },
  });

  const handleSubmit = (data: BudgetFormData) => {
    updateBudgetCap.mutate(
      { amount: data.budget, month: month, year: year },
      {
        onSuccess: (response) => {
          onSuccess();
          showToast(
            t("admin.client.clientBudgetAction.success", { action: "updated" }),
            "success"
          );
          queryClient.setQueryData(
            campaignJobBoardQueryKeys.clientBudgetMonth.detail(clientId, year, month),
            (oldData?: GetClientResponse) => {
              return {
                ...(oldData || {}),
                allocableBudget: response.data.allocableBudget,
                allocatedBudget: response.data.allocatedBudget,
                budgetCap: response.data.budgetCap,
                clientId2: response.data.clientId2,
                spent: response.data.spent,
              };
            }
          );
        },
      }
    );
  };

  useEffect(() => {
    if (updateBudgetCap.isPending) {
      disableClose();
    } else {
      enableClose();
    }
  }, [updateBudgetCap.isPending, disableClose, enableClose]);

  const errorCode = updateBudgetCap.error?.response?.data?.[0]?.errorCode;

  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t("actions.edit", { entity: t("common.budgetCap") })}
        </h3>
      </div>
      <BudgetForm
        error={errorCode && t(`errorCodes.${errorCode}`)}
        isLoading={updateBudgetCap.isPending}
        label={t("common.budget")}
        methods={methods}
        onSubmit={handleSubmit}
      />

      <div className={styles.actions}>
        <Button variant="outline" onClick={closePopup}>
          {t("common.cancel")}
        </Button>
        <Button
          loading={updateBudgetCap.isPending}
          onClick={methods.handleSubmit(handleSubmit)}
        >
          <Icons.AddIcon fontSize={24} />
          {t("actions.save", { entity: t("common.budget") })}
        </Button>
      </div>
    </>
  );
};

export const EditBudgetCapPopup = (props: EditBudgetCapPopupProps) => {
  return (
    <ClientBudgetMonthProvider
      clientId={props.clientId}
      month={props.month}
      year={props.year}
    >
      <EditBudgetCapPopupInner {...props} />
    </ClientBudgetMonthProvider>
  );
};
